import type { EscalationContacts } from 'api/escalationContacts.types'
import type { Dispatch, SetStateAction } from 'react'
import { useEffect, useRef, useState } from 'react'

// Order-sensitive equality for the id arrays the pickers hold — backs the
// hydration bailout below so a re-seed with the same values is a no-op.
const sameIds = (a: number[], b: number[]): boolean =>
  a.length === b.length && a.every((id, index) => id === b[index])

export type HydratedContactSelection = {
  primaryIds: number[]
  additionalIds: number[]
  setPrimaryIds: Dispatch<SetStateAction<number[]>>
  setAdditionalIds: Dispatch<SetStateAction<number[]>>
}

/**
 * Owns the EscalationContacts pane's local recipient selection and seeds it
 * from the saved set whenever a fresh set arrives and no save is in flight
 * (mirrors EscalationRulesPane's rehydrate-when-quiescent). Extracted from the
 * pane so the "hydration + value-equality bailout + isSavingRef" cluster lives
 * in one tested unit rather than swelling the pane (AI-32 review).
 */
export function useHydratedContactSelection(
  contacts: EscalationContacts | undefined,
  isSaving: boolean
): HydratedContactSelection {
  const [primaryIds, setPrimaryIds] = useState<number[]>([])
  const [additionalIds, setAdditionalIds] = useState<number[]>([])

  // "Latest value" mirror of the in-flight flag so the hydration effect (deps:
  // [contacts]) can read it without re-running when it flips — rehydrating
  // mid-save would clobber what the merchant just submitted.
  const isSavingRef = useRef(false)
  isSavingRef.current = isSaving

  useEffect(() => {
    if (!contacts) return
    if (isSavingRef.current) return
    const nextPrimary =
      contacts.primaryUserId != null ? [contacts.primaryUserId] : []
    const nextAdditional = contacts.additionalUserIds
    // Value-equality bailout (mirrors EscalationRulesPane): returning `prev`
    // lets React skip the re-render, so a query hook that ever yields a fresh
    // `data` object each render can't drive an effect→setState→effect loop.
    setPrimaryIds(prev => (sameIds(prev, nextPrimary) ? prev : nextPrimary))
    setAdditionalIds(prev =>
      sameIds(prev, nextAdditional) ? prev : nextAdditional
    )
  }, [contacts])

  return { primaryIds, additionalIds, setPrimaryIds, setAdditionalIds }
}
